// CommunityHero.tsx — "Build. Belong. Thrive." directory hero.
import styled from 'styled-components';
import { Button } from '@/ui';
import { query } from '@/theme/breakpoints';

const Hero = styled.section`
  position: relative; overflow: hidden;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: linear-gradient(135deg, ${({ theme }) => theme.color.surface}, ${({ theme }) => theme.color.surfaceRaised});
  padding: 28px 24px; margin-bottom: 18px;
  display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap;
`;
const Copy = styled.div`max-width: 460px; display: flex; flex-direction: column; gap: 8px;`;
const Title = styled.h2`
  margin: 0; font-family: ${({ theme }) => theme.font.display}; font-weight: 800;
  font-size: ${({ theme }) => theme.fontSize.xl}; color: ${({ theme }) => theme.color.textPrimary};
`;
const Sub = styled.p`margin: 0; font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};`;
const Actions = styled.div`display: flex; gap: 10px; margin-top: 6px;`;
// Decorative "community network" motif — a teal node-graph (connected nodes =
// communities). Replaces an empty radial-glow blob that read as a broken image.
// Hidden below lg: on tablet/mobile the hero wraps and a lone 160px box looked
// like a failed image load, so it's desktop-only chrome.
const Art = styled.div`
  width: 160px; height: 110px; flex: 0 0 auto;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surfaceRaised};
  display: flex; align-items: center; justify-content: center; overflow: hidden;
  svg { width: 100%; height: 100%; display: block; }
  .edge { stroke: ${({ theme }) => theme.color.accentCyan}; stroke-width: 1.25; stroke-linecap: round; }
  .node { fill: ${({ theme }) => theme.color.surfaceRaised}; stroke: ${({ theme }) => theme.color.accentCyan}; stroke-width: 1.5; }
  .hub { fill: ${({ theme }) => theme.color.accentCyan}; }
  @media ${query.tabletDown} { display: none; }
`;

export interface CommunityHeroProps {
  onJoin: () => void;
  onViewMine: () => void;
}

export function CommunityHero({ onJoin, onViewMine }: CommunityHeroProps) {
  return (
    <Hero>
      <Copy>
        <Title>Build. Belong. Thrive.</Title>
        <Sub>Join communities that match your interests and share your world.</Sub>
        <Actions>
          <Button variant="primary" onClick={onJoin}>Join Communities</Button>
          <Button variant="secondary" onClick={onViewMine}>View My Communities</Button>
        </Actions>
      </Copy>
      <Art aria-hidden="true">
        <svg viewBox="0 0 160 110" role="presentation" focusable="false">
          <g className="edge" opacity="0.55">
            <line x1="80" y1="55" x2="32" y2="26" />
            <line x1="80" y1="55" x2="128" y2="28" />
            <line x1="80" y1="55" x2="26" y2="84" />
            <line x1="80" y1="55" x2="134" y2="82" />
            <line x1="80" y1="55" x2="80" y2="96" />
          </g>
          <g className="edge" opacity="0.28">
            <line x1="32" y1="26" x2="128" y2="28" />
            <line x1="26" y1="84" x2="134" y2="82" />
          </g>
          <g className="node">
            <circle cx="32" cy="26" r="6" />
            <circle cx="128" cy="28" r="6" />
            <circle cx="26" cy="84" r="6" />
            <circle cx="134" cy="82" r="6" />
            <circle cx="80" cy="96" r="6" />
          </g>
          <circle className="hub" cx="80" cy="55" r="10" />
        </svg>
      </Art>
    </Hero>
  );
}
